import * as Sentry from '@sentry/react';
import { FormEvent, useState } from 'react';

import { useResendCountdown } from '@/auth/components/shared';
import { CognitoSignInError } from '@/auth/utils/cognitoSignIn';
import { UnauthorizedError } from '@/core/api';
import ERROR_CODES from '@/core/apiErrorCodes';

export interface FieldErrors {
  form?: string;
  code?: string;
  password?: string;
}

/**
 * Report to Sentry by error name only. Cognito's messages (and causes) can
 * echo what was submitted, e.g. a rejected password in a validation error.
 */
export function reportResetError(err: unknown): void {
  const name = err instanceof Error ? err.name : typeof err;
  const sanitized = new Error(`Password reset failed: ${name}`);
  sanitized.name = 'CognitoPasswordResetError';
  Sentry.captureException(sanitized, { tags: { cognito_error: name } });
}

/** The reset errors a form shows; anything unexpected goes to Sentry. */
export function toFieldErrors(err: unknown): FieldErrors {
  if (err instanceof CognitoSignInError) {
    switch (err.code) {
      case ERROR_CODES.AUTH_OTP_INVALID:
        return { code: 'This code is invalid or has expired. Request a new one.' };
      case ERROR_CODES.AUTH_PASSWORD_REQUIREMENTS:
        return { password: 'Password does not meet the requirements' };
      case ERROR_CODES.AUTH_PASSWORD_REUSED:
        return { password: 'Cannot reuse a recent password.' };
      case ERROR_CODES.AUTH_TOO_MANY_ATTEMPTS:
        return { form: 'Too many attempts. Please wait a few minutes and try again.' };
    }
  }
  reportResetError(err);
  return { form: 'Sorry, an error occurred. Please try again.' };
}

interface ConfirmResetOptions {
  email: string;
  confirmReset: (email: string, code: string, newPassword: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  requestCode: (email: string) => Promise<void>;
  /** The password is set but the sign-in after it stopped (MFA, network). */
  onSignInStopped: () => void;
}

interface ConfirmReset {
  code: string;
  setCode: (code: string) => void;
  password: string;
  setPassword: (password: string) => void;
  errors: FieldErrors;
  busy: boolean;
  resent: boolean;
  /** Seconds until "Resend code" is available again. */
  secondsLeft: number;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleResend: () => Promise<void>;
}

/** State and handlers for the confirm step: code plus new password, then sign-in. */
export function useConfirmReset({
  email,
  confirmReset,
  signIn,
  requestCode,
  onSignInStopped,
}: ConfirmResetOptions): ConfirmReset {
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  // A code was just sent when this step mounts; the same pacing for every address.
  const { secondsLeft, restart } = useResendCountdown();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const newPassword = password;
    setBusy(true);
    setErrors({});
    try {
      await confirmReset(email, code, newPassword);
    } catch (err) {
      setErrors(toFieldErrors(err));
      setBusy(false);
      return;
    }
    // Set: the field no longer needs to hold it.
    setPassword('');
    await signIn(email, newPassword).catch((err: unknown) => {
      if (!(err instanceof UnauthorizedError)) reportResetError(err);
      onSignInStopped();
    });
  }

  async function handleResend() {
    setBusy(true);
    setErrors({});
    setResent(false);
    try {
      await requestCode(email);
      setResent(true);
      restart();
    } catch (err) {
      setErrors(toFieldErrors(err));
    }
    setBusy(false);
  }

  return {
    code,
    setCode,
    password,
    setPassword,
    errors,
    busy,
    resent,
    secondsLeft,
    handleSubmit,
    handleResend,
  };
}
